import { useMemo } from 'react';
import { pick, prop } from 'js/utils/ramda.util';
import { useUserSelector } from 'js/store/hooks';
import { useFormatter } from 'js/hooks/useFormatter';
import { useComponentConfig } from 'js/hooks/component/useComponentConfig';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import { useVisualDates } from '../../useVisualDates';
import { useDynamicReducerSelector } from '../../../store';

export const useExportSummary = () => {
    const formatter = useFormatter();
    const translate = useComponentTranslations();
    const userName = useUserSelector(prop('userName'));
    const { showTotalTransactionHistory, showBeginEndBalanceEnabled } = useComponentConfig();
    const {
        summary, startingBalances, endingBalances
    } = useDynamicReducerSelector(pick([
        'summary', 'startingBalances', 'endingBalances'
    ]));
    const { startTime, endTime } = useVisualDates();
    const startDate = formatter.date(startTime);
    const endDate = formatter.date(endTime);
    const totalPeriod = translate('summary.title.periods', [startDate, endDate]);

    const totalBalances = useMemo(() => {
        if (!summary) {
            return null;
        }

        const { totalWins, totalBets, totalDeposits, totalApprovedWithdraws } = summary;

        return showTotalTransactionHistory
            ? {
                [translate('total.title.wins')]: totalWins,
                [translate('total.title.bets')]: totalBets,
                [translate('total.title.deposits')]: totalDeposits,
                [translate('total.title.withdraws')]: totalApprovedWithdraws
            } : null;
    }, [summary, translate, showTotalTransactionHistory]);

    const startingEndingBalances = useMemo(() => showBeginEndBalanceEnabled
        ? {
            [translate('summary.title.starting')]: startingBalances,
            [translate('summary.title.ending')]: endingBalances
        } : null,
        [startingBalances, endingBalances, translate, showBeginEndBalanceEnabled]);

    return useMemo(() => {
        const dateRange = { [translate('total.title.summary')]: totalPeriod };
        const playerName = [{ [translate('summary.title.player')]: userName }];
        const balances = [{ ...dateRange, ...totalBalances, ...startingEndingBalances }];

        return { balances, playerName };
    }, [translate, userName, totalPeriod, totalBalances, startingEndingBalances]);
};
